import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { OMNET_IDS } from "../../../constants/permisos";
import { supabase } from "../../../utils";
import { OMNET_CARGO } from "../hooks/useMapaCalorAgenciasData";
import IndicadorCalor from "./IndicadorCalor";

const COLORES_BG = {
  verde: "#d4edda",
  amarillo: "#fff3cd",
  rojo: "#f8d7da",
};

const BORDER = "1px solid #999";
const BORDER_GRUPO = "2px solid #888";
const GRIS_BG = "#e9ecef";

const formatFecha = (lastDate) => {
  if (!lastDate) return null;
  const d = new Date(lastDate);
  if (isNaN(d.getTime())) return null;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const CELL = {
  border: BORDER,
  fontSize: ".8rem",
  textAlign: "left",
  padding: "8px 7px",
  overflowWrap: "break-word",
  wordBreak: "break-word",
  whiteSpace: "normal",
  overflow: "hidden",
};

// ─── Celda de contacto con nombre + fecha + color ────────────────────────────
const ContactoCell = ({ contacto, rowSpan, style }) => {
  if (!contacto) {
    return (
      <td rowSpan={rowSpan} style={{ ...CELL, backgroundColor: GRIS_BG, color: "#999", ...style }}>
        –
      </td>
    );
  }
  const fecha = formatFecha(contacto.lastDate);
  return (
    <td rowSpan={rowSpan} style={{ ...CELL, backgroundColor: COLORES_BG[contacto.color], ...style }}>
      {contacto.nombre} {contacto.apellido}
      {fecha && <span style={{ fontSize: ".6rem", color: "#666", marginLeft: 4 }}>{fecha}</span>}
    </td>
  );
};

// ─── Celda individual de miembro (sin rolShort, ya está en su columna) ───────
const PersonaCell = ({ persona, rowSpan, colorOverride, style }) => {
  if (!persona) {
    return (
      <td rowSpan={rowSpan} style={{ ...CELL, backgroundColor: GRIS_BG, color: "#999", ...style }}>
        –
      </td>
    );
  }
  const fecha = formatFecha(persona.lastDate);
  const bg = COLORES_BG[colorOverride || persona.color];
  return (
    <td rowSpan={rowSpan} style={{ ...CELL, backgroundColor: bg, ...style }}>
      {persona.nombre} {persona.apellido}
      {fecha && <span style={{ fontSize: ".6rem", color: "#666", marginLeft: 4 }}>{fecha}</span>}
    </td>
  );
};

// ─── Detección de holding por dominio de email ──────────────────────────────
const DOMAIN_HOLDING_MAP = {
  publicis: "PUBLICIS",
  starcom: "PUBLICIS",
  zenith: "PUBLICIS",
  spark: "PUBLICIS",
  digitas: "PUBLICIS",
  omd: "OMNICOM",
  omnicom: "OMNICOM",
  phd: "OMNICOM",
  omg: "OMNICOM",
  hearts: "OMNICOM",
  dentsu: "DENTSU",
  carat: "DENTSU",
  iprospect: "DENTSU",
  havas: "HAVAS",
  arena: "HAVAS",
  ipg: "IPG",
  initiative: "IPG",
  mediabrands: "IPG",
  um: "IPG",
  wpp: "WPP",
  groupm: "WPP",
  mindshare: "WPP",
  wavemaker: "WPP",
  essence: "WPP",
};

const getHoldingFromEmail = (email) => {
  if (!email) return null;
  const domain = email.split("@")[1]?.toLowerCase() || "";
  for (const [keyword, holding] of Object.entries(DOMAIN_HOLDING_MAP)) {
    if (domain.includes(keyword)) return holding;
  }
  return null;
};

const getHoldingFromInterm = (intermNombre) => {
  if (!intermNombre) return null;
  const upper = intermNombre.split("|")[0].trim().toUpperCase();
  const HOLDINGS = ["IPG", "OMNICOM", "DENTSU", "HAVAS", "PUBLICIS", "WPP"];
  return HOLDINGS.find((h) => upper.includes(h)) || null;
};

// ─── Roles y componentes de edición ─────────────────────────────────────────
const CARGO_OPTIONS = ["Gerente", "Director", "Supervisor", "Planificador", "Asistente"];

const ContactRow = ({ row, idx, opciones, opcionesExtra, disabledIds, onUpdate, onRemove, expectedHolding }) => {
  // Buscar el usuario seleccionado para detectar mismatch de holding
  const selectedUser = row.usuario_id
    ? [...(opciones || []), ...(opcionesExtra || [])].find((u) => u.id === row.usuario_id)
    : null;
  const emailHolding = selectedUser ? getHoldingFromEmail(selectedUser.email) : null;
  const isMismatch = expectedHolding && emailHolding && emailHolding !== expectedHolding;

  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <select
          value={row.cargo}
          onChange={(e) => onUpdate(idx, "cargo", e.target.value)}
          style={{ fontSize: ".65rem", padding: "2px 3px", width: 80, borderRadius: 4, border: "1px solid #ccc" }}
        >
          {CARGO_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={row.usuario_id || ""}
          onChange={(e) => onUpdate(idx, "usuario_id", e.target.value ? Number(e.target.value) : null)}
          style={{
            fontSize: ".65rem",
            padding: "2px 3px",
            flex: 1,
            borderRadius: 4,
            border: isMismatch ? "2px solid #dc3545" : "1px solid #ccc",
          }}
        >
          <option value="">— seleccionar —</option>
          {opciones.map((u) => (
            <option key={u.id} value={u.id} disabled={disabledIds.includes(u.id)}>
              {u.nombre} {u.apellido}
              {u.email ? ` (${u.email})` : ""}
            </option>
          ))}
          {opcionesExtra && opcionesExtra.length > 0 && (
            <>
              <option disabled>── Otros ──</option>
              {opcionesExtra.map((u) => (
                <option key={u.id} value={u.id} disabled={disabledIds.includes(u.id)}>
                  {u.nombre} {u.apellido}
                  {u.email ? ` (${u.email})` : ""}
                </option>
              ))}
            </>
          )}
        </select>
        <button
          onClick={() => onRemove(idx)}
          style={{
            fontSize: ".7rem",
            padding: "1px 5px",
            border: "1px solid #ccc",
            borderRadius: 4,
            backgroundColor: "#fff",
            cursor: "pointer",
            color: "#c00",
          }}
          title="Quitar"
        >
          x
        </button>
      </div>
      {isMismatch && (
        <div
          style={{
            fontSize: ".6rem",
            color: "#dc3545",
            fontWeight: 600,
            marginTop: 2,
            marginLeft: 84,
          }}
        >
          {`\u26A0 Email es de ${emailHolding}, pero la agencia es ${expectedHolding}`}
        </div>
      )}
    </div>
  );
};

// ─── Popover de edición de equipo para un cliente ───────────────────────────
const EditPopover = ({ cli, agUsuarios, agUsuariosExtra, onClose, onSaved, portalPos }) => {
  const ref = useRef(null);
  const currentContacts = cli.contactosAgencia || [];

  // Inicializar filas desde contactos existentes
  const [rows, setRows] = useState(() => {
    if (currentContacts.length === 0) return [{ cargo: "Director", usuario_id: null }];
    return currentContacts.map((c) => ({
      cargo: c.cargoNorm || c.cargo || "Asistente",
      usuario_id: c.id,
    }));
  });
  const [saving, setSaving] = useState(false);

  // Cerrar al click fuera
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const updateRow = (idx, field, value) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));
  const addRow = () => setRows((prev) => [...prev, { cargo: "Asistente", usuario_id: null }]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const validRows = rows.filter((r) => r.usuario_id);

      // 1. Eliminar contactos actuales de este cliente (preservar OMNET)
      await supabase.removeWhere("clientes_contactos_agencia", `cliente_id=eq.${cli.id}&cargo=neq.${OMNET_CARGO}`);

      // 2. Insertar nuevos contactos (bulk insert)
      if (validRows.length > 0) {
        await supabase.insert(
          "clientes_contactos_agencia",
          validRows.map((r) => ({
            cliente_id: cli.id,
            usuario_id: r.usuario_id,
            cargo: r.cargo,
          }))
        );
      }

      onSaved();
      onClose();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(`Error al guardar: ${err?.message || JSON.stringify(err)}`);
      setSaving(false);
    }
  };

  // IDs ya seleccionados (para deshabilitar en otros dropdowns)
  const selectedIds = rows.filter((r) => r.usuario_id).map((r) => r.usuario_id);
  const expectedHolding = getHoldingFromInterm(cli.intermNombre);

  return (
    <div
      ref={ref}
      style={{
        position: portalPos ? "fixed" : "absolute",
        zIndex: 1000,
        top: portalPos?.openUp ? undefined : portalPos ? portalPos.top : "100%",
        bottom: portalPos?.openUp ? portalPos.bottom : undefined,
        left: portalPos ? portalPos.left : 0,
        backgroundColor: "#fff",
        border: "1px solid #ccc",
        borderRadius: 8,
        padding: 10,
        boxShadow: "0 4px 12px rgba(0,0,0,.15)",
        minWidth: 320,
        maxHeight: portalPos ? "calc(100vh - 60px)" : undefined,
        overflowY: portalPos ? "auto" : undefined,
      }}
    >
      <div style={{ fontSize: ".72rem", fontWeight: 700, marginBottom: 6 }}>{clienteLabel(cli)}</div>
      {rows.map((row, idx) => (
        <ContactRow
          key={idx}
          row={row}
          idx={idx}
          opciones={agUsuarios}
          opcionesExtra={agUsuariosExtra}
          disabledIds={selectedIds.filter((id) => id !== row.usuario_id)}
          onUpdate={updateRow}
          onRemove={removeRow}
          expectedHolding={expectedHolding}
        />
      ))}
      <button
        onClick={addRow}
        style={{
          fontSize: ".62rem",
          padding: "2px 8px",
          borderRadius: 4,
          border: "1px dashed #0d6efd",
          backgroundColor: "#fff",
          color: "#0d6efd",
          cursor: "pointer",
          marginBottom: 6,
        }}
      >
        + Agregar contacto
      </button>
      <div style={{ display: "flex", gap: 6 }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            fontSize: ".68rem",
            padding: "3px 10px",
            borderRadius: 4,
            border: "none",
            backgroundColor: "#0d6efd",
            color: "#fff",
            cursor: "pointer",
          }}
        >
          {saving ? "Guardando..." : "Guardar"}
        </button>
        <button
          onClick={onClose}
          style={{
            fontSize: ".68rem",
            padding: "3px 10px",
            borderRadius: 4,
            border: "1px solid #ccc",
            backgroundColor: "#fff",
            cursor: "pointer",
          }}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
};

// ─── Helper: label de cliente (Empresa | Producto si son distintos) ──────────
const clienteLabel = (cli) => {
  const emp = (cli.empresa || "").trim();
  const prod = (cli.producto || "").trim();
  if (!emp && !prod) return "—";
  if (!emp || !prod || emp.toUpperCase() === prod.toUpperCase()) return emp || prod;
  return `${emp} | ${prod}`;
};

// ─── Chip de cliente con semáforo + drag ─────────────────────────────────────
const ClienteChip = ({ cli, editMode, agUsuarios, agUsuariosExtra, onSaved }) => {
  const [open, setOpen] = useState(false);
  const chipRef = useRef(null);
  const [popoverPos, setPopoverPos] = useState(null);
  const bg = COLORES_BG[cli.colorAgencia] || COLORES_BG.rojo;
  const handleClose = useCallback(() => setOpen(false), []);

  const handleDragStart = (e) => {
    e.dataTransfer.setData("text/plain", String(cli.id));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleClick = () => {
    if (!editMode) return;
    if (open) {
      setOpen(false);
      return;
    }
    if (chipRef.current) {
      const rect = chipRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const estimatedH = 250;
      if (spaceBelow < estimatedH) {
        // Abrir hacia arriba
        setPopoverPos({ bottom: window.innerHeight - rect.top + 4, left: rect.left, openUp: true });
      } else {
        setPopoverPos({ top: rect.bottom + 4, left: rect.left, openUp: false });
      }
    }
    setOpen(true);
  };

  return (
    <span style={{ display: "inline-block" }}>
      <span
        ref={chipRef}
        draggable={editMode}
        onDragStart={editMode ? handleDragStart : undefined}
        onClick={handleClick}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "2px 7px",
          borderRadius: 4,
          backgroundColor: bg,
          fontSize: ".72rem",
          marginRight: 4,
          marginBottom: 3,
          whiteSpace: "nowrap",
          border: editMode ? "1px dashed #0d6efd" : "1px solid rgba(0,0,0,.08)",
          cursor: editMode ? "grab" : "default",
        }}
      >
        <IndicadorCalor color={cli.colorAgencia} size={8} />
        {clienteLabel(cli)}
        {cli.categoria_cliente && (
          <span style={{ fontSize: ".6rem", fontWeight: 700, color: "#555" }}>{cli.categoria_cliente}</span>
        )}
        {editMode && <span style={{ fontSize: ".6rem", marginLeft: 2 }}>✎</span>}
      </span>
      {open &&
        popoverPos &&
        createPortal(
          <EditPopover
            cli={cli}
            agUsuarios={agUsuarios || []}
            agUsuariosExtra={agUsuariosExtra || []}
            onClose={handleClose}
            onSaved={onSaved}
            portalPos={popoverPos}
          />,
          document.body
        )}
    </span>
  );
};

// ─── Calcular rows que ocupa un equipo ───────────────────────────────────────
const getEquipoRows = (eq) => Math.max(1, eq.supervisores.length, eq.planificadores.length, eq.asistentes.length);

// ─── Calcular rowSpan total para holding y agencia ───────────────────────────
const getTotalRows = (equipos) => equipos.reduce((sum, eq) => sum + getEquipoRows(eq), 0);

// ─── Agrupar equipos consecutivos por director (para rowSpan) ─────────────────
const getDirectorGroups = (equipos) => {
  const groups = [];
  equipos.forEach((eq) => {
    const dirId = eq.director ? String(eq.director.id) : "sin_director";
    const last = groups.at(-1);
    if (last && last.dirId === dirId) {
      last.equipos.push(eq);
      last.totalRows += getEquipoRows(eq);
    } else {
      groups.push({ dirId, director: eq.director, equipos: [eq], totalRows: getEquipoRows(eq) });
    }
  });
  return groups;
};

// ─── Aplanar director group en filas con merge info para Sup/Plan/Asis ───────
const COL_PRIORITY = { rojo: 3, amarillo: 2, verde: 1 };

const flattenDirectorGroup = (dirGroup) => {
  const rows = [];
  dirGroup.equipos.forEach((eq) => {
    const numRows = getEquipoRows(eq);
    for (let r = 0; r < numRows; r++) {
      rows.push({
        equipo: eq,
        isEquipoFirst: r === 0,
        equipoTotalRows: numRows,
        // Para overflow rows (r>0), si el slot está vacío heredar de index 0
        // Así el merge agrupa correctamente a través de overflow rows
        sup: eq.supervisores[r] || (r > 0 && eq.supervisores[0]) || null,
        plan: eq.planificadores[r] || (r > 0 && eq.planificadores[0]) || null,
        asis: eq.asistentes[r] || (r > 0 && eq.asistentes[0]) || null,
      });
    }
  });

  // Para cada columna de rol, merge celdas consecutivas de la misma persona
  const mergeColumn = (getter) => {
    const n = rows.length;
    const spans = Array.from({ length: n }, () => 1);
    const colors = rows.map((r) => getter(r)?.color || null);
    for (let i = n - 2; i >= 0; i--) {
      const curr = getter(rows[i]);
      const next = getter(rows[i + 1]);
      if (curr && next && curr.id === next.id) {
        spans[i] = spans[i + 1] + 1;
        spans[i + 1] = 0; // merged into cell above
        if ((COL_PRIORITY[colors[i + 1]] || 0) > (COL_PRIORITY[colors[i]] || 0)) {
          colors[i] = colors[i + 1];
        }
      }
    }
    return { spans, colors };
  };

  const supM = mergeColumn((r) => r.sup);
  const planM = mergeColumn((r) => r.plan);
  const asisM = mergeColumn((r) => r.asis);

  return rows.map((row, i) => ({
    ...row,
    supSpan: supM.spans[i],
    supColor: supM.colors[i],
    planSpan: planM.spans[i],
    planColor: planM.colors[i],
    asisSpan: asisM.spans[i],
    asisColor: asisM.colors[i],
  }));
};

// ─── Celda OMNET (por equipo) ────────────────────────────────────────────────
// eslint-disable-next-line arrow-body-style
const OmnetCell = ({ clientes, equipoTotalRows, editMode, omnetUsuarios, onSaveOmnet }) => {
  // Modo lectura: consolidar contactos OMNET únicos del equipo
  if (!editMode) {
    const omnetMap = {};
    clientes.forEach((cli) => {
      if (cli.omnetContacto) omnetMap[cli.omnetContacto.id] = cli.omnetContacto;
    });
    const unicos = Object.values(omnetMap);

    if (unicos.length === 0) {
      return (
        <td rowSpan={equipoTotalRows} style={{ ...CELL, backgroundColor: GRIS_BG, color: "#999" }}>
          –
        </td>
      );
    }
    // Un solo contacto → celda tipo PersonaCell
    if (unicos.length === 1) {
      const omnet = unicos[0];
      const fecha = formatFecha(omnet.lastDate);
      return (
        <td rowSpan={equipoTotalRows} style={{ ...CELL, backgroundColor: COLORES_BG[omnet.color] }}>
          {omnet.nombre} {omnet.apellido}
          {fecha && <span style={{ fontSize: ".6rem", color: "#666", marginLeft: 4 }}>{fecha}</span>}
        </td>
      );
    }
    // Múltiples distintos → listar cada uno
    return (
      <td rowSpan={equipoTotalRows} style={{ ...CELL, verticalAlign: "middle" }}>
        {unicos.map((omnet) => {
          const fecha = formatFecha(omnet.lastDate);
          return (
            <div
              key={omnet.id}
              style={{
                backgroundColor: COLORES_BG[omnet.color],
                borderRadius: 4,
                padding: "2px 5px",
                marginBottom: 2,
                fontSize: ".8rem",
              }}
            >
              {omnet.nombre} {omnet.apellido}
              {fecha && <span style={{ fontSize: ".6rem", color: "#666", marginLeft: 4 }}>{fecha}</span>}
            </div>
          );
        })}
      </td>
    );
  }

  // Modo edición: dropdown por cliente
  return (
    <td rowSpan={equipoTotalRows} style={{ ...CELL, verticalAlign: "middle", fontSize: ".72rem" }}>
      {clientes.map((cli) => {
        const omnet = cli.omnetContacto;
        return (
          <div key={cli.id} style={{ marginBottom: 3, display: "flex", alignItems: "center", gap: 3 }}>
            <span style={{ fontSize: ".6rem", color: "#666", maxWidth: 60, overflow: "hidden", whiteSpace: "nowrap" }}>
              {(cli.empresa || cli.producto || "").substring(0, 10)}
            </span>
            <select
              value={omnet?.id || ""}
              onChange={(e) => onSaveOmnet(cli.id, e.target.value ? Number(e.target.value) : null)}
              style={{
                fontSize: ".62rem",
                padding: "1px 3px",
                flex: 1,
                borderRadius: 4,
                border: "1px solid #ccc",
              }}
            >
              <option value="">— sin OMNET —</option>
              {omnetUsuarios.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nombre} {u.apellido}
                </option>
              ))}
            </select>
          </div>
        );
      })}
    </td>
  );
};

// ─── Celda de clientes con drop target ────────────────────────────────────────
const DropTargetCell = ({ equipo, equipoTotalRows, editMode, agUsuarios, agUsuariosExtra, onSaved, onDropCliente }) => {
  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const handleDragEnter = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  const handleDragLeave = () => setDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const clienteId = Number(e.dataTransfer.getData("text/plain"));
    if (clienteId && onDropCliente) onDropCliente(clienteId, equipo);
  };

  return (
    <td
      rowSpan={equipoTotalRows}
      onDragOver={editMode ? handleDragOver : undefined}
      onDragEnter={editMode ? handleDragEnter : undefined}
      onDragLeave={editMode ? handleDragLeave : undefined}
      onDrop={editMode ? handleDrop : undefined}
      style={{
        ...CELL,
        verticalAlign: "middle",
        borderLeft: BORDER_GRUPO,
        ...(dragOver ? { backgroundColor: "#d0e8ff", outline: "2px dashed #0d6efd" } : {}),
        transition: "background-color .15s",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {equipo.clientes.map((cli) => (
          <ClienteChip
            key={cli.id}
            cli={cli}
            editMode={editMode}
            agUsuarios={agUsuarios}
            agUsuariosExtra={agUsuariosExtra}
            onSaved={onSaved}
          />
        ))}
      </div>
    </td>
  );
};

// ─── Fila plana (1 <tr> por fila, con merge info para Sup/Plan/Asis) ─────────
const FilaPlana = ({
  row,
  isFirstEquipo,
  isFirstDirector,
  directorRowSpan,
  agNombre,
  agTotalClientes,
  agColor,
  agTotalEquipos,
  agGerentes,
  holdingNombre,
  holdingColor,
  holdingTotalAgencias,
  holdingGerentes,
  onDropCliente,
  isFirstHolding,
  editMode,
  agUsuarios,
  agUsuariosExtra,
  onSaved,
  isOmnicom,
  omnetUsuarios,
  onSaveOmnet,
  isSingleAgencia,
}) => {
  // Si el holding tiene una sola agencia con el mismo nombre, fusionar columnas
  const mergeHoldingAgencia = isSingleAgencia;

  return (
    <tr style={isFirstEquipo ? { borderTop: BORDER_GRUPO } : undefined}>
      {/* Holding (+ Agencia fusionada si es agencia única) */}
      {isFirstHolding && (
        <td
          rowSpan={holdingTotalAgencias}
          colSpan={mergeHoldingAgencia ? 2 : 1}
          style={{
            ...CELL,
            backgroundColor: COLORES_BG[holdingColor],
            fontWeight: 700,
            fontSize: ".8rem",
            verticalAlign: "middle",
            lineHeight: 1.2,
          }}
        >
          {holdingNombre}
          {mergeHoldingAgencia && (
            <span style={{ fontSize: ".65rem", color: "#777", marginLeft: 5 }}>({agTotalClientes})</span>
          )}
          {holdingGerentes && holdingGerentes.length > 0 && (
            <div style={{ marginTop: 4 }}>
              {holdingGerentes.map((g) => (
                <div
                  key={g.id}
                  style={{
                    fontSize: ".65rem",
                    fontWeight: 400,
                    color: "#555",
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    marginTop: 2,
                  }}
                >
                  <IndicadorCalor color={g.color} size={6} />
                  {g.nombre} {g.apellido}
                </div>
              ))}
            </div>
          )}
          {mergeHoldingAgencia && agGerentes.length > 0 && (
            <div style={{ marginTop: 4 }}>
              {agGerentes.map((g) => (
                <div
                  key={g.id}
                  style={{
                    fontSize: ".65rem",
                    fontWeight: 400,
                    color: "#555",
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    marginTop: 2,
                  }}
                >
                  <IndicadorCalor color={g.color} size={6} />
                  {g.nombre} {g.apellido}
                  {g.cargo_detalle && <span style={{ fontStyle: "italic", color: "#888" }}>{g.cargo_detalle}</span>}
                </div>
              ))}
            </div>
          )}
        </td>
      )}

      {/* Agencia hija (solo si NO están fusionadas) */}
      {!mergeHoldingAgencia && isFirstEquipo && (
        <td
          rowSpan={agTotalEquipos}
          style={{
            ...CELL,
            backgroundColor: COLORES_BG[agColor],
            fontWeight: 600,
            verticalAlign: "middle",
          }}
        >
          {agNombre}
          <span style={{ fontSize: ".65rem", color: "#777", marginLeft: 5 }}>({agTotalClientes})</span>
          {agGerentes.length > 0 && (
            <div style={{ marginTop: 4 }}>
              {agGerentes.map((g) => (
                <div
                  key={g.id}
                  style={{
                    fontSize: ".65rem",
                    fontWeight: 400,
                    color: "#555",
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    marginTop: 2,
                  }}
                >
                  <IndicadorCalor color={g.color} size={6} />
                  {g.nombre} {g.apellido}
                  {g.cargo_detalle && <span style={{ fontStyle: "italic", color: "#888" }}>{g.cargo_detalle}</span>}
                </div>
              ))}
            </div>
          )}
        </td>
      )}

      {/* Director */}
      {isFirstDirector && (
        <ContactoCell
          contacto={row.equipo.director}
          rowSpan={directorRowSpan}
          style={{ fontWeight: 600, borderLeft: BORDER_GRUPO }}
        />
      )}

      {/* Supervisor — rowSpan merge */}
      {row.supSpan > 0 && <PersonaCell persona={row.sup} rowSpan={row.supSpan} colorOverride={row.supColor} />}

      {/* Planificador — rowSpan merge */}
      {row.planSpan > 0 && <PersonaCell persona={row.plan} rowSpan={row.planSpan} colorOverride={row.planColor} />}

      {/* Asistente — rowSpan merge */}
      {row.asisSpan > 0 && <PersonaCell persona={row.asis} rowSpan={row.asisSpan} colorOverride={row.asisColor} />}

      {/* Clientes — solo primera fila de cada equipo (drop target en editMode) */}
      {row.isEquipoFirst && (
        <DropTargetCell
          equipo={row.equipo}
          equipoTotalRows={row.equipoTotalRows}
          editMode={editMode}
          agUsuarios={agUsuarios}
          agUsuariosExtra={agUsuariosExtra}
          onSaved={onSaved}
          onDropCliente={onDropCliente}
        />
      )}

      {/* OMNET — solo Omnicom, primera fila de cada equipo */}
      {isOmnicom && row.isEquipoFirst && (
        <OmnetCell
          clientes={row.equipo.clientes}
          equipoTotalRows={row.equipoTotalRows}
          editMode={editMode}
          omnetUsuarios={omnetUsuarios}
          onSaveOmnet={onSaveOmnet}
        />
      )}
    </tr>
  );
};

// Número total de columnas: 7 base + 1 para OMNET en Omnicom
const TOTAL_COLS = 7;
const TOTAL_COLS_OMNICOM = 8;

// ─── Tabla principal ─────────────────────────────────────────────────────────
const TablaCalorAgencias = ({ data, editMode, usuariosPorAgencia, allUsuarios, onRefetch }) => {
  const handleSaved = useCallback(() => {
    if (onRefetch) onRefetch();
  }, [onRefetch]);

  // OMNET: usuarios disponibles para asignar
  const omnetUsuarios = (allUsuarios || []).filter((u) => OMNET_IDS.includes(u.id));

  // Guardar/quitar asignación OMNET para un cliente
  const handleSaveOmnet = useCallback(
    async (clienteId, omnetUserId) => {
      try {
        // Eliminar OMNET actual de este cliente
        await supabase.removeWhere("clientes_contactos_agencia", `cliente_id=eq.${clienteId}&cargo=eq.${OMNET_CARGO}`);
        if (omnetUserId) {
          await supabase.insert("clientes_contactos_agencia", {
            cliente_id: clienteId,
            usuario_id: omnetUserId,
            cargo: OMNET_CARGO,
          });
        }
        if (onRefetch) onRefetch();
      } catch (err) {
        const isDuplicate = err?.message?.includes("duplicate") || err?.code === "23505";
        if (isDuplicate) {
          const usr = (allUsuarios || []).find((u) => u.id === omnetUserId);
          const nombre = usr ? `${usr.nombre} ${usr.apellido}` : "";
          // eslint-disable-next-line no-alert
          alert(
            `${nombre} ya está asignado/a como contacto regular de este cliente. ` +
              "Para asignarlo como OMNET, primero quitalo del equipo de compra tradicional."
          );
        } else {
          // eslint-disable-next-line no-alert
          alert(`Error al guardar OMNET: ${err?.message || JSON.stringify(err)}`);
        }
        if (onRefetch) onRefetch();
      }
    },
    [onRefetch, allUsuarios]
  );

  // Drag & drop: mover cliente a otro equipo
  const handleDropCliente = useCallback(
    async (clienteId, targetEquipo) => {
      // Ya pertenece a este equipo? → no hacer nada
      if (targetEquipo.clientes.some((c) => c.id === clienteId)) return;

      const contacts = [];
      if (targetEquipo.director) {
        contacts.push({ cliente_id: clienteId, usuario_id: targetEquipo.director.id, cargo: "Director" });
      }
      (targetEquipo.supervisores || []).forEach((s) => {
        contacts.push({ cliente_id: clienteId, usuario_id: s.id, cargo: "Supervisor" });
      });
      (targetEquipo.planificadores || []).forEach((p) => {
        contacts.push({ cliente_id: clienteId, usuario_id: p.id, cargo: "Planificador" });
      });
      (targetEquipo.asistentes || []).forEach((a) => {
        contacts.push({ cliente_id: clienteId, usuario_id: a.id, cargo: "Asistente" });
      });

      try {
        await supabase.removeWhere("clientes_contactos_agencia", `cliente_id=eq.${clienteId}&cargo=neq.${OMNET_CARGO}`);
        if (contacts.length > 0) {
          await supabase.insert("clientes_contactos_agencia", contacts);
        }
        if (onRefetch) onRefetch();
      } catch (err) {
        // eslint-disable-next-line no-alert
        alert(`Error al mover cliente: ${err?.message || JSON.stringify(err)}`);
      }
    },
    [onRefetch]
  );
  if (data.length === 0) {
    return <p className="text-muted text-center mt-4">No hay datos para mostrar</p>;
  }

  return (
    <div style={{ overflowY: "auto", maxHeight: "calc(100vh - 140px)" }}>
      {data.map((holding) => {
        const holdingTotalRows = holding.agencias.reduce((s, ag) => s + getTotalRows(ag.equipos), 0);
        let isFirstHolding = true;
        const isOmnicom = holding.nombre.toUpperCase() === "OMNICOM";
        const isSingleAg = holding.agencias.length === 1;
        const cols = isOmnicom ? TOTAL_COLS_OMNICOM : TOTAL_COLS;

        return (
          <div
            key={holding.nombre}
            style={{
              borderRadius: 12,
              marginBottom: 16,
              border: "1px solid #ccc",
              boxShadow: "0 1px 4px rgba(0,0,0,.08)",
            }}
          >
            <table
              className="table table-sm align-middle mb-0"
              style={{
                fontSize: ".85rem",
                borderCollapse: "collapse",
                tableLayout: "fixed",
                width: "100%",
              }}
            >
              <colgroup>
                <col style={{ width: "9%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "11%" }} />
                <col />
                {isOmnicom && <col style={{ width: "11%" }} />}
              </colgroup>
              <thead>
                <tr>
                  <th
                    colSpan={cols}
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      backgroundColor: "#222",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: ".8rem",
                      padding: "10px 12px",
                      letterSpacing: ".3px",
                      borderRadius: "12px 12px 0 0",
                    }}
                  >
                    {holding.nombre}
                    <span style={{ fontWeight: 400, fontSize: ".7rem", marginLeft: 8, opacity: 0.7 }}>
                      {holding.totalClientes} clientes
                    </span>
                  </th>
                </tr>
                <tr style={{ backgroundColor: "#f0f0f0" }}>
                  {[
                    ...(isSingleAg ? [{ label: "Agencia", colSpan: 2 }] : [{ label: "Holding" }, { label: "Agencia" }]),
                    { label: "Director", borderLeft: BORDER_GRUPO },
                    { label: "Supervisor" },
                    { label: "Planificador" },
                    { label: "Asistente" },
                    { label: "Clientes", borderLeft: BORDER_GRUPO },
                    ...(isOmnicom ? [{ label: "OMNET", borderLeft: BORDER_GRUPO }] : []),
                  ].map((col) => (
                    <th
                      key={col.label}
                      colSpan={col.colSpan || 1}
                      style={{
                        ...CELL,
                        fontWeight: 600,
                        fontSize: ".7rem",
                        padding: "8px 7px",
                        position: "sticky",
                        top: 37,
                        zIndex: 10,
                        backgroundColor: "#f0f0f0",
                        borderBottom: "2px solid #aaa",
                        ...(col.borderLeft ? { borderLeft: col.borderLeft } : {}),
                      }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {holding.agencias.map((ag) => {
                  const agTotalRows = getTotalRows(ag.equipos);
                  const dirGroups = getDirectorGroups(ag.equipos);
                  let isFirstEquipo = true;

                  // Recopilar usuarios por intermId: agencia + holding padre
                  const lookupIds = new Set(ag.intermIds || []);
                  (holding.holdingIntermIds || []).forEach((id) => lookupIds.add(id));
                  const uMap = usuariosPorAgencia || {};
                  const agUsers = [...lookupIds].flatMap((id) => uMap[id] || []);
                  const seen = new Set();
                  const agUsuariosUnicos = agUsers
                    .filter((u) => {
                      if (seen.has(u.id)) return false;
                      seen.add(u.id);
                      return true;
                    })
                    .sort((a, b) => `${a.nombre} ${a.apellido}`.localeCompare(`${b.nombre} ${b.apellido}`));
                  // Usuarios restantes (no en esta agencia) para el dropdown "Otros"
                  const agUsuariosExtra = (allUsuarios || [])
                    .filter((u) => !seen.has(u.id))
                    .sort((a, b) => `${a.nombre} ${a.apellido}`.localeCompare(`${b.nombre} ${b.apellido}`));

                  return dirGroups.flatMap((grp) => {
                    const flatRows = flattenDirectorGroup(grp);
                    return flatRows.map((row, rowIdx) => {
                      const isFirstDir = rowIdx === 0;
                      const el = (
                        <FilaPlana
                          key={`${ag.nombre}-${grp.dirId}-${rowIdx}`}
                          row={row}
                          isFirstEquipo={isFirstEquipo}
                          isFirstDirector={isFirstDir}
                          directorRowSpan={grp.totalRows}
                          agNombre={ag.nombre}
                          agTotalClientes={ag.totalClientes}
                          agColor={ag.color}
                          agTotalEquipos={agTotalRows}
                          agGerentes={ag.gerentes || []}
                          holdingNombre={holding.nombre}
                          holdingColor={holding.color}
                          holdingTotalAgencias={holdingTotalRows}
                          holdingGerentes={holding.gerentes || []}
                          isFirstHolding={isFirstHolding}
                          editMode={editMode}
                          agUsuarios={agUsuariosUnicos}
                          agUsuariosExtra={agUsuariosExtra}
                          onSaved={handleSaved}
                          onDropCliente={handleDropCliente}
                          isOmnicom={isOmnicom}
                          omnetUsuarios={omnetUsuarios}
                          onSaveOmnet={handleSaveOmnet}
                          isSingleAgencia={holding.agencias.length === 1}
                        />
                      );
                      isFirstHolding = false;
                      isFirstEquipo = false;
                      return el;
                    });
                  });
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

export default TablaCalorAgencias;
